import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { Loader } from "lucide-react";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUpdateSKUMutation } from "@/data/mutations";
import { BuilderIOTabOptions } from "@/data/types";
import { updateBuilderIOTabOptions } from "@/lib/cms";

import { useCMSContext } from "../cms-context";
import { useCMSTabContext } from "../cms-tab-context";

const cmsBuilderTabFormSchema = z.object({
  builder_io_path: z.string().min(1),
  builder_io_api_key: z.string().min(1),
});

type Props = {
  options: BuilderIOTabOptions;
};

export const BuilderIOTabSettings: FC<Props> = ({ options }) => {
  const form = useForm<z.infer<typeof cmsBuilderTabFormSchema>>({
    resolver: zodResolver(cmsBuilderTabFormSchema),
    defaultValues: {
      builder_io_path: get(options, "builder_io_path", ""),
      builder_io_api_key: get(options, "builder_io_api_key", ""),
    },
  });

  const { sku, pageContent } = useCMSContext();
  const { tabIndex } = useCMSTabContext();
  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const onSubmit = (data: z.infer<typeof cmsBuilderTabFormSchema>) => {
    updateSKUMutation.mutate({
      page_content: updateBuilderIOTabOptions(pageContent, data, tabIndex),
    });
  };

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Settings</h4>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="text-sm space-y-4">
            <FormField
              control={form.control}
              name="builder_io_path"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Builder.io Path</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="This is the builder.io path for the embedded content."
                      {...field}
                    />
                  </FormControl>
                  <Link
                    to="https://www.builder.io/c/docs/targeting#target-options-by-plan"
                    target="_blank"
                  >
                    <FormDescription className="underline">
                      Where to find the Builder.io path
                    </FormDescription>
                  </Link>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="builder_io_api_key"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Builder.io API Key</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="The API key can be found in the Environments section of the Settings page."
                      {...field}
                    />
                  </FormControl>
                  <Link
                    to="https://www.builder.io/c/docs/using-your-api-key"
                    target="_blank"
                  >
                    <FormDescription className="underline">
                      Where to find the Builder.io API key
                    </FormDescription>
                  </Link>
                </FormItem>
              )}
            />
          </CardContent>
          <div className="border-t px-6 py-4">
            <Button type="submit" disabled={updateSKUMutation.isPending}>
              {updateSKUMutation.isPending ? (
                <Loader className="animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};
